import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PublicToolsMenu from '../components/public/PublicToolsMenu';
import { AskAvailabilityButton, SavePhotoButton, resolveImage } from '../components/public/CatalogHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'wholesale_token';

const wax = axios.create({ baseURL: API });
wax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

function ItemCard({ item, levelLabel }) {
  const img = resolveImage(item.image_url);
  const savings = item.public_price > 0 ? Math.round((1 - item.level_price / item.public_price) * 100) : 0;
  return (
    <article className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition" data-testid={`wholesale-card-${item.id}`}>
      <div className="aspect-square bg-slate-100 relative overflow-hidden">
        {img ? (
          <img src={img} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300">📦</div>
        )}
        {savings > 0 && (
          <div className="absolute top-2 left-2 bg-purple-600 text-white text-[10px] font-bold rounded-full px-2 py-1 shadow-md">
            -{savings}% vs público
          </div>
        )}
        {item.kind === 'package' && (
          <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold rounded-full px-2 py-1 shadow-md">
            📦 PAQUETE
          </div>
        )}
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        <h3 className="font-bold text-slate-900 leading-tight break-words">{item.name}</h3>

        {/* Precio mayorista */}
        <div>
          <p className="text-[10px] uppercase tracking-wide text-purple-600 font-semibold">Tu precio ({levelLabel})</p>
          <p className="text-2xl font-bold text-purple-700 leading-none">{money(item.level_price)}</p>
        </div>

        {/* Precio público sugerido */}
        {item.public_price > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Precio público (sugerido)</p>
            <p className="text-sm font-bold text-slate-700">{money(item.public_price)}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-1.5 mt-auto pt-2">
          <AskAvailabilityButton productName={item.name} price={item.level_price} />
          <SavePhotoButton imageUrl={item.image_url} filename={item.name} />
        </div>
      </div>
    </article>
  );
}

function LoginScreen({ onOk }) {
  const [nip, setNip] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(nip)) { setError('El NIP debe ser 4 dígitos'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await wax.post('/public/wholesale/login', { nip });
      localStorage.setItem(TOKEN_KEY, data.token);
      onOk(data.client);
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-slate-900 flex items-center justify-center px-4">
      <PublicToolsMenu current="/mayorista" />
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="wholesale-login-form">
        <div className="text-center">
          <div className="text-5xl mb-2">🏬</div>
          <h1 className="text-xl font-bold text-slate-800">Portal Mayorista</h1>
          <p className="text-sm text-slate-500 mt-1">Ingresa tu NIP para ver tu catálogo</p>
        </div>
        <input
          type="password" inputMode="numeric" maxLength={4}
          placeholder="NIP (4 dígitos)"
          value={nip}
          onChange={(e) => setNip(e.target.value.replace(/\D/g, '').slice(0, 4))}
          className="w-full text-center text-2xl tracking-widest font-bold py-3 border-2 border-slate-200 focus:border-purple-500 rounded-xl outline-none"
          data-testid="wholesale-nip-input"
          autoFocus
        />
        {error && <p className="text-sm text-rose-600 text-center" data-testid="wholesale-login-error">{error}</p>}
        <button type="submit" disabled={loading || nip.length !== 4} className="w-full bg-purple-700 hover:bg-purple-800 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl transition" data-testid="wholesale-login-btn">
          {loading ? 'Entrando…' : 'Entrar'}
        </button>
        <p className="text-[11px] text-center text-slate-400">
          Solo clientes con precio de mayoreo o medio mayoreo. Pide tu NIP en tienda.
        </p>
      </form>
    </div>
  );
}

export default function WholesalePortal() {
  const [client, setClient] = useState(null);
  const [items, setItems] = useState(null);
  const [levelLabel, setLevelLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setLoading(false); return; }
    (async () => {
      try {
        const meRes = await wax.get('/public/wholesale/me');
        setClient(meRes.data);
        await loadCatalog();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setClient(null);
      } finally { setLoading(false); }
    })();
  }, []);

  const loadCatalog = async () => {
    try {
      const { data } = await wax.get('/public/wholesale/catalog');
      setItems(data.items || []);
      setLevelLabel(data.client?.level_label || '');
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar el catálogo');
    }
  };

  const onLogin = async (c) => {
    setClient(c);
    setLoading(true);
    await loadCatalog();
    setLoading(false);
  };

  const handleLogout = async () => {
    try { await wax.post('/public/wholesale/logout'); } catch { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    setClient(null);
    setItems(null);
  };

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => (i.name || '').toLowerCase().includes(q)) : items;
  }, [items, query]);

  if (!client) return <LoginScreen onOk={onLogin} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicToolsMenu current="/mayorista" />

      <header
        className="bg-gradient-to-br from-purple-700 to-purple-900 text-white shadow"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)' }}
      >
        <div className="max-w-5xl mx-auto px-4 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest opacity-80">Portal Mayorista</p>
              <h1 className="text-2xl sm:text-3xl font-bold truncate" data-testid="wholesale-name">🏬 {client.name}</h1>
              <p className="text-xs opacity-80 mt-1">Precio {levelLabel} · {items?.length || 0} productos disponibles</p>
            </div>
            <button onClick={handleLogout} className="text-xs bg-purple-900/50 hover:bg-purple-900 px-3 py-1.5 rounded-full flex-shrink-0" data-testid="wholesale-logout-btn">Salir</button>
          </div>
          <input
            type="search" placeholder="🔍 Buscar…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mt-3 w-full bg-white/95 text-slate-900 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-400"
            data-testid="wholesale-search"
          />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm mb-4" data-testid="wholesale-error">{error}</div>
        )}
        {loading && !items && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="aspect-square bg-slate-100 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-6 bg-slate-100 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}
        {items && filtered.length === 0 && (
          <div className="text-center py-16" data-testid="wholesale-empty">
            <div className="text-5xl mb-2">🏬</div>
            <p className="text-slate-500">Sin productos disponibles con tu nivel de precio.</p>
          </div>
        )}
        {items && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="wholesale-grid">
            {filtered.map((it) => <ItemCard key={`${it.kind}-${it.id}`} item={it} levelLabel={levelLabel} />)}
          </div>
        )}
      </main>

      <footer className="text-center text-[11px] text-slate-400 py-6">
        Portal Mayorista · Precios sin promoción · Sistema POS
      </footer>
    </div>
  );
}
